import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatMoney } from "@/lib/api";
import { RoasBadge, RevenueSourceChip, roasLegend, MetricLabel } from "@/lib/metrics";
import RoasBasisToggle, { readStoredBasis } from "@/components/RoasBasisToggle";
import TimeRangePicker from "@/components/TimeRangePicker";
import { TrendingUp } from "lucide-react";
import { resolveRange } from "@/lib/dateRange";
import RoasSparkline from "@/components/RoasSparkline";

export function rangeToDates(range, start, end) {
  const r = resolveRange(range, start, end);
  return { since: r.since, until: r.until };
}

// Shared with /meta-poc ROAS card: revenue of properties that HAVE revenue ÷ those properties' spend
// (properties without revenue and 未歸屬 spend are excluded from the denominator).
export function computeRoasTotal(rows) {
  const withRev = (rows || []).filter((r) => r.revenue != null);
  const revenue = withRev.reduce((s, r) => s + (r.revenue || 0), 0);
  const spend = withRev.reduce((s, r) => s + (r.spend || 0), 0);
  return { roas: withRev.length && spend > 0 ? revenue / spend : null, revenue, spend, withRev, source: mergeSource(withRev) };
}

export function mergeSource(rows) {
  const s = new Set(rows.map((r) => r.revenue_source).filter(Boolean));
  if (s.size === 0) return null;
  if (s.size === 1) return [...s][0];
  return "mixed";
}

export default function HomeRoasSection() {
  const [range, setRange] = useState("30d");
  const [custom, setCustom] = useState({ start: null, end: null });
  const [basis, setBasis] = useState(() => readStoredBasis());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (range === "custom" && (!custom.start || !custom.end)) return;
    let cancel = false;
    setData(null);
    const { since, until } = rangeToDates(range, custom.start, custom.end);
    api.get("/compare/multi-property", { params: { since, until, revenue_basis: basis } })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ properties: [], unattributed: {} }); });
    return () => { cancel = true; };
  }, [range, custom.start, custom.end, basis]);

  const rows = data?.properties || [];
  const { roas: totalRoas, revenue: totalRev, spend: totalSpendWithRev, withRev } = computeRoasTotal(rows);
  const totalSpend = rows.reduce((s, r) => s + (r.spend || 0), 0) + (data?.unattributed?.spend || 0);
  const sorted = [...rows].sort((a, b) => (b.roas ?? -1) - (a.roas ?? -1));

  return (
    <Card className="rounded-2xl bg-white border-slate-200 shadow-sm" data-testid="home-roas-section">
      <CardHeader className="flex-row items-start justify-between space-y-0 gap-3 flex-wrap">
        <div>
          <CardTitle className="text-slate-900 text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#F26A1F]" />
            <MetricLabel label="ROAS 總覽" hintKey="roas" testid="home-roas-info"
              notes={data ? [
                ...(totalRoas == null ? ["這段期間沒有營收資料 → 到「PMS 整合」連線，或在「場域設定」手填月營收"] : []),
                ...(mergeSource(withRev) === "manual" ? ["PMS 未連線，使用手填月營收（依天數攤提），請確認數字是否最新"] : []),
                ...(mergeSource(withRev) === "mixed" ? ["部分營收來自 PMS、部分是手填，兩種來源混合計算"] : []),
                ...(totalRoas != null && rows.some((p) => p.revenue == null && (p.spend || 0) > 0) ? [`有 ${rows.filter((p) => p.revenue == null && (p.spend || 0) > 0).length} 個有花費的場域沒有營收資料，沒有算進 ROAS`] : []),
              ] : undefined} />
          </CardTitle>
          <CardDescription className="text-slate-500 text-xs mt-1" data-testid="home-roas-legend">燈號:{roasLegend()} · 依{basis === "booking" ? "進單日" : "住宿日"}營業額</CardDescription>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TimeRangePicker value={range} syncUrl={false} testid="home-roas-range"
            onChange={({ range: r, start, end }) => { setRange(r); setCustom(r === "custom" ? { start, end } : { start: null, end: null }); }} />
          <RoasBasisToggle value={basis} onChange={setBasis} size="sm" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data ? <Skeleton className="h-24 w-full bg-slate-100" data-testid="home-roas-skeleton" /> : (
          <>
            <div className="flex items-end gap-4 flex-wrap" data-testid="home-roas-total">
              <RoasBadge roas={totalRoas} size="lg" showLabel prefix="" testid="home-roas-total-badge" />
              <RevenueSourceChip source={mergeSource(withRev)} testid="home-roas-total-source" />
              <div className="text-xs text-slate-700 font-mono" data-testid="home-roas-total-spend">Meta 總花費 {formatMoney(totalSpend, "TWD")}</div>
              <div className="text-xs text-slate-500 font-mono">
                營業額 {formatMoney(totalRev, "TWD")} ÷ 有營收場域花費 {formatMoney(totalSpendWithRev, "TWD")} · {withRev.length}/{rows.length} 場域有營收
              </div>
            </div>
            {(data?.unattributed?.spend || 0) > 0 && (
              <Link to="/meta-poc" title="無法對應任何場域的活動花費，不計入場域 ROAS" className="inline-flex text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 w-fit hover:bg-amber-100" data-testid="home-roas-unattributed">
                未歸屬花費 {formatMoney(data.unattributed.spend, "TWD")} →
              </Link>
            )}
            <details className="rounded-lg border border-slate-200" data-testid="home-roas-details">
              <summary className="cursor-pointer px-3 py-2 text-xs text-slate-600">查看明細（每日趨勢、各場域 ROAS）</summary>
              <div className="p-3 pt-1 space-y-3">
            <div className="max-w-xl" data-testid="home-roas-trend">
              <RoasSparkline testid="home-roas-spark" params={{ ...rangeToDates(range, custom.start, custom.end), revenue_basis: basis }} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="home-roas-grid">
              {sorted.map((p) => (
                <div key={p.id} className="rounded-lg border border-slate-200 p-2.5 space-y-1" data-testid={`home-roas-card-${p.id}`}>
                  <div className="text-xs font-semibold text-slate-800 truncate">{p.name}</div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <RoasBadge roas={p.roas} prefix="" testid={`home-roas-badge-${p.id}`} />
                    <RevenueSourceChip source={p.revenue_source} testid={`home-roas-src-${p.id}`} />
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono">花費 {formatMoney(p.spend, "TWD")}{p.revenue != null ? ` · 營收 ${formatMoney(p.revenue, "TWD")}` : ""}</div>
                </div>
              ))}
            </div>
              </div>
            </details>
          </>
        )}
      </CardContent>
    </Card>
  );
}
